import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/button-variants";
import {
  Actions,
  BorderNode,
  type Node,
  type ITabSetRenderValues,
  Layout,
  TabNode,
  TabSetNode,
} from "flexlayout-react";
import "flexlayout-react/style/light.css";
import {
  ExpandIcon,
  Grid2X2PlusIcon,
  ShrinkIcon,
  XIcon,
} from "lucide-react";
import { useCallback } from "react";
import { useActiveLayout } from "./layout-active-context";
import styles from "./layout.module.css";

const classNames: Record<string, (defaultClassName: string) => string> = {
  flexlayout__layout: (defaultClassName: string) => {
    return `${defaultClassName} ${styles.layout}`;
  },
  flexlayout__tabset_tabbar_outer: (defaultClassName: string) => {
    return `${defaultClassName} flex overflow-hidden text-xs border-b-1`;
  },
  flexlayout__tabset_tabbar_inner_tab_container: () => {
    return `relative flex items-center whitespace-nowrap py-2 pl-2`;
  },
  flexlayout__tabset_tabbar_inner_tab_container_top: () => {
    return "";
  },
  flexlayout__tab_toolbar_sticky_buttons_container: () => {
    return "flex items-center gap-1 pl-1";
  },
  // flexlayout__mini_scrollbar_container: () => {
  //   return "flex grow-1";
  // },
  // flexlayout__tab_toolbar: () => {
  //   return `flex items-center gap-1 pl-1`;
  // },

  flexlayout__tabset_tab_divider: () => {
    return "w-1";
  },
  flexlayout__tab_toolbar: () => {
    return "flex items-center gap-1 pl-1 p-2";
  },
  flexlayout__tab_button: () => {
    return buttonVariants({
      variant: "secondary",
      size: "sm",
      className: `text-xs group`,
    });
  },
  "flexlayout__tab_button--unselected": () => {
    return "!text-muted-foreground";
  },
  "flexlayout__tab_button--selected": () => {
    return "is-selected";
  },
  flexlayout__tab_button_trailing: () => {
    return "hidden group-[.is-selected]:flex";
  },
  flexlayout__tab_toolbar_button: () => {
    return buttonVariants({
      variant: "ghost",
      size: "icon-sm",
    });
  },
  "flexlayout__tab_toolbar_button-min": () => {
    return "";
  },
  "flexlayout__tab_toolbar_button-max": () => {
    return "";
  },
};

type PanelLayoutProps = {
  disablePanelControls?: boolean;
};

export const PanelLayout = ({ disablePanelControls = false }: PanelLayoutProps) => {
  const { model, config, layoutRef, onModelChange } =
    useActiveLayout();

  const factory = useCallback(
    (node: TabNode) => {
      const panelTypeId = node.getComponent();
      if (!panelTypeId) {
        console.error(`Panel not found for node ${node.getId()}`);
        return null;
      }

      const panelConfig = config[panelTypeId];
      if (!panelConfig) {
        console.error(`Panel "${panelTypeId}" is invalid`);
        return null;
      }

      return <panelConfig.component panelId={node.getId()} />;
    },
    [config]
  );

  const onRenderTabSet = useCallback(
    (node: TabSetNode | BorderNode, renderValues: ITabSetRenderValues) => {
      const selectedNode = node.getSelectedNode();
      if (!selectedNode) {
        return;
      }

      const isTab = isTabNode(selectedNode);
      if (!isTab) {
        console.error(`Selected node is not a tab: ${selectedNode.getId()}`);
        return;
      }

      const panelTypeId = selectedNode.getComponent();
      if (!panelTypeId) {
        console.error(`Panel not found for node ${selectedNode.getId()}`);
        return;
      }

      const panelConfig = config[panelTypeId];
      if (!panelConfig) {
        console.error(`Panel "${panelTypeId}" is invalid`);
        return;
      }

      // Dynamically control the maximize button based on the selected tab's panel config
      const enableMaximize = panelConfig.enableMaximize ?? false;
      
      // Check if this is a TabSetNode (not BorderNode) before checking/updating enableMaximize
      if ('isEnableMaximize' in node) {
        const currentEnableMaximize = node.isEnableMaximize();
        
        // Only update if the value needs to change to avoid unnecessary model updates
        if (currentEnableMaximize !== enableMaximize) {
          model.doAction(
            Actions.updateNodeAttributes(node.getId(), { enableMaximize })
          );
        }
      }

      // Hide header for panels with enableHeader: false
      const enableHeader = panelConfig.enableHeader ?? true;
      if (!enableHeader) {
        // Add a marker so CSS can hide the header visually but keep it in DOM
        renderValues.buttons.push(
          <div
            key="header-marker"
            data-no-header="true"
            style={{ display: "none" }}
          />
        );
        return;
      }

      if (disablePanelControls) {
        return;
      }

      // Add close button to the buttons array
      renderValues.buttons.push(
        <Button
          variant="ghost"
          size="icon-sm"
          key="close-button"
          onClick={() => {
            model.doAction(Actions.deleteTab(selectedNode.getId()));
          }}
        >
          <XIcon className="size-3" />
        </Button>
      );
    },
    [config, disablePanelControls, model]
  );

  const onTabSetPlaceHolder = useCallback(() => {
    return (
      <div className="text-xs w-full h-full flex items-center justify-center">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Click</span>
          <Button size="sm" variant="outline" className="text-xs" disabled>
            <Grid2X2PlusIcon className="size-4" />
            Add Panel
          </Button>
          <span className="text-muted-foreground">
            in the top-right to start building your layout
          </span>
        </div>
      </div>
    );
  }, []);

  return (
    <Layout
      ref={layoutRef}
      model={model}
      factory={factory}
      classNameMapper={(defaultClassName) => {
        const overrideFn = classNames[defaultClassName];
        if (overrideFn) {
          return overrideFn(defaultClassName);
        }

        return defaultClassName;
      }}
      icons={{
        close: () => {
          return null;
        },
        restore: () => {
          return <ShrinkIcon className="size-3" />;
        },
        maximize: () => {
          return <ExpandIcon className="size-3" />;
        },
        more: (_, hiddenTabs) => {
          return (
            <div className="text-xs font-medium">+{hiddenTabs.length}</div>
          );
        },
      }}
      onRenderTabSet={onRenderTabSet}
      onTabSetPlaceHolder={onTabSetPlaceHolder}
      onModelChange={onModelChange}
    />
  );
};

const isTabNode = (node: Node): node is TabNode => {
  return node.getType() === "tab";
};
